<template lang="pug">
  dv-server-form(
    ref="form"
    v-model="form"
    :method="method"
    :schema="schema"
    :success-callback="successCallback"
    :error-callback="errorCallback"
    v-slot="{ fields, submit, loading }"
  )
    v-window(v-model="step")
      v-window-item(:value="STEPS.phone")
        .d-flex.flex-column.gap-16
          p.opacity-70.mb-0 Для {{ isAttaching ? 'привязки сервиса' : 'авторизации в кабинете участника мероприятий' }} используйте номер телефона, привязанный к Вашему аккаунту в Max
          dv-form-field(:field="fields.phone")

      v-window-item(:value="STEPS.bot")
        .d-flex.flex-column.gap-32
          dv-alert(
            v-if="!isSent"
            text
            icon="$ui-information-circle"
            color="ui-warning darken-1"
          )
            h3 Для получения кода запустите чат с официальным ботом “Афиша мероприятий”
            p.ui-body-2.mt-2
              | Чтобы получить код для входа, пожалуйста, запустите чат с ботом.
              | Он может попросить Вас поделиться номером телефона, это нужно исключительно,
              | чтобы распознавать вас как пользователя системы.
          p.opacity-70.mb-0(v-else)
            span Бот “Афиша мероприятий” в мессенджере Max код для {{ isAttaching ? 'привязки' : 'авторизации' }} на указанный Вами номер&nbsp;
            span.weight-600 {{ numberText }}.

          app-avatar.align-self-center(
            :size="250"
            :avatar="qrCodeMax"
          )

          p.ui-caption.px-8.text-center.mb-0
            span.opacity-70
              | Отсканируйте QR-код для запуска чата с ботом #[br]
              | или перейдите по ссылке&nbsp;
            a.ui-primary--text(
              v-if="maxUrl"
              :href="maxUrl"
              target="_blank"
            ) {{ maxUrl }}

      v-window-item(:value="STEPS.code")
        .d-flex.flex-column.gap-32
          p.opacity-70.mb-0
            span Бот “Афиша мероприятий” в мессенджере Max код для {{ isAttaching ? 'привязки' : 'авторизации' }} на указанный Вами номер&nbsp;
            span.weight-600 {{ numberText }}.
          dv-alert(
            text
            icon="$ui-information-circle"
            color="ui-info"
          )
            h3 Введите код подтверждения для {{ isAttaching ? 'привязки' : 'авторизации' }} в Системе.
            p.ui-body-2.mt-1 Код должен был прийти в чате {{ maxUrl }}
          dv-btn(
            text
            color="ui-info"
            @click="phoneStep"
          ) Изменить телефон
          v-sheet.mx-auto.my-6(max-width="400px")
            dv-form-field(:field="fields.code")
            d-timer(
              :seconds="60"
              v-slot="{ init: startTimer, timeLeft }"
            )
              server-data(
                :method="sendCode"
                :success-callback="() => startTimer()"
                no-init
                v-slot="{ init, loading: sendingCode }"
              )
                dv-btn.ui-body-2(
                  :disabled="!!timeLeft"
                  :loading="sendingCode"
                  text
                  color="ui-info"
                  :height="28"
                  @click="init"
                ) Отправить код ещё раз{{ timeLeft ? `: ${timeLeft}` : '' }}
    v-expand-transition
      div(v-if="error")
        dv-alert.my-6(
          text
          icon="$ui-attention-mini"
          color="ui-error"
        )
          h3 Ошибка
          p.ui-body-2.mt-1 {{ error }}
    .d-flex.align-center.mt-6
      dv-btn.flex-1(
        large
        :loading="loading"
        @click="submit"
      ) {{ nextBtnText }}
    user-agreement-text.text-center.mx-auto.mt-5(v-if="step === STEPS.code")
</template>

<script>
import DvAlert from '@/packages/ui/components/alerts/DvAlert.vue';
import DvBtn from '@/packages/ui/components/buttons/DvBtn.vue';
import DvFormField from '@/packages/ui/components/forms/DvFormField.vue';
import DvServerForm from '@/packages/ui/components/forms/DvServerForm.vue';
import DvPhoneField from '@/packages/ui/components/inputs/DvPhoneField.vue';
import BotMaxQrCode from '@/assets/bot-max-qr-code.png';
import AppAvatar from '@/components/user-redesign/AppAvatar.vue';
import DTimer from '@/packages/ui/components/timers/DTimer.vue';
import UserAgreementText from '../Texts/UserAgreementText.vue';
import { customApiService } from '@/services/APIService.js';
import { USER_DUPLICATE_STATUS_KEY } from '@/modules/UserDuplicatesMerges/components/constants.js';

const CODE_LENGTH = 7;

const STEPS = {
  phone: 1,
  bot: 2,
  code: 3,
};

export default {
  components: {
    DvServerForm,
    DvFormField,
    DvBtn,
    DvAlert,
    AppAvatar,
    DTimer,
    UserAgreementText,
  },
  props: {
    isAttaching: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      STEPS,
      step: STEPS.phone,
      maxUrl: undefined,
      isSent: false,
      form: {},

      error: undefined,
    };
  },
  computed: {
    qrCodeMax() {
      return {
        url: BotMaxQrCode,
      };
    },
    nextBtnText() {
      if (this.step === STEPS.phone) return 'Прислать код подтверждения в Max';
      if (this.step === STEPS.bot) return this.isSent ? `${this.isAttaching ? 'Продолжить' : 'Авторизоваться'}` : 'Чат запущен, с ботом познакомились';
      return this.isAttaching ? 'Привязать аккаунт' : 'Авторизоваться';
    },
    numberText() {
      const { phone } = this.form;
      const number = String(phone).replace(/[^0-9]/g, '');
      return `+${number.substring(0, 1)} (${number.substring(1, 4)}) ${number.substring(4, 7)}-${number.substring(7, 9)}-${number.substring(9, 11)}`;
    },
    schema() {
      return [
        {
          value: 'phone',
          name: 'phone',
          label: 'Номер телефона',
          rules: 'required|phone',
          component: DvPhoneField,
          props: {
            height: 44,
            autocomplete: 'phone',
          },
        },
        {
          value: 'code',
          name: 'code',
          label: 'Код подтверждения',
          rules: 'required',
          component: 'v-otp-input',
          props: {
            length: CODE_LENGTH,
            type: `number|length:${CODE_LENGTH}`,
          },
        },
      ];
    },
  },
  methods: {
    phoneStep() {
      this.step = STEPS.phone;
      this.form.code = undefined;
      this.maxUrl = undefined;
      this.isSent = false;
    },
    successCallback(data) {
      if (this.step === STEPS.phone || this.step === STEPS.bot) {
        const { bot_url, is_sent, is_met } = data?.data || {};

        if (bot_url) {
          this.maxUrl = bot_url;
        }

        this.isSent = Boolean(is_sent) || Boolean(is_met);
        if (this.step === STEPS.bot && !this.isSent) {
          this.$toast.warning('Бот ещё не подтвердил отправку кода');
        }
        this.step = this.isSent ? STEPS.code : STEPS.bot;
        return;
      }
      if (this.isAttaching) {
        this.$emit('close', data);
        return;
      }
      this.$emit('auth', data);
      this.$emit('close', data);
      this.$toast.success(`Вы успешно ${this.isAttaching ? 'привязали аккаунт Max' : 'авторизовались'}!`);
    },
    errorCallback({ data }) {
      this.error = data?.response?.data?.data?.message;
    },
    async method() {
      this.error = undefined;
      return {
        [STEPS.phone]: this.sendCode,
        [STEPS.bot]: this.checkIsMet,
        [STEPS.code]: this.authByMax,
      }[this.step]?.();
    },
    sendCode() {
      const { phone } = this.form;
      return this.$rApi.user.simpleAuthByMaxSendCode({ data: { phone } });
    },
    checkIsMet() {
      const { phone } = this.form;
      return this.$rApi.user.simpleAuthByMaxIsMet({ data: { phone } });
    },
    async authByMax() {
      const data = {
        ...this.form,
        is_attaching: this.isAttaching,
      };
      const { response, error } = await this.$rApi.user.simpleAuthByMax({ data });
      if (!error && this.isAttaching) {
        const { access_token } = response?.data?.data;
        const apiByNewUser = customApiService(localStorage.getItem('active_url_system') || process.env.VUE_APP_API_URL);
        apiByNewUser.axios.interceptors.request.use((config) => {
          config.headers.Authorization = access_token ? `Bearer ${access_token}` : 'sdfsaf';
          return config;
        });
        const answer = await apiByNewUser.get('/user');
        const newUser = answer?.data?.data;
        const duplicateAnswer = await apiByNewUser.get('/user-duplicates', {
          'search[users_exists]': 1,
          'search[status]': [USER_DUPLICATE_STATUS_KEY.wait].join(','),
        });
        const [duplicate] = duplicateAnswer?.data?.data || [];
        if ((newUser?.id !== this.$user?.data?.id) && duplicate) {
          this.$dialogs.confirm({
            attrs: {
              'max-width': 600,
              method: () => {
                this.$router.go();
              },
              closeIcon: '',
              persistent: true,
              messageAttrs: {
                class: 'ui-body-1',
              },
              message:
                'Внимание! Вы привязываете к себе способ входа, используемый для авторизации в другом аккаунте.<br/><br/>' +
                'Если продолжить, будет инициировано объединение учётных записей',
              closeBtnAttrs: {
                'btn-text': 'Отменить',
                height: 44,
                dark: false,
                large: true,
                outlined: true,
                color: 'ui-primary',
                class: 'rounded-10 flex-1 ui-body-1 px-12 ml-0 mt-5 mr-4',
              },
              confirmBtnAttrs: {
                'btn-text': 'Продолжить',
                height: 44,
                dark: false,
                large: true,
                class: 'ui-primary rounded-10 flex-1 ui-bright--text ui-body-1 px-12 ml-0 mt-5',
              },
            },
            listeners: {
              cancel: () => {
                this.$models.users.duplicates.cancel({ args: [duplicate.id] });
              },
            },
          });
        }
      }
      return { response, error };
    },
  },
};
</script>

<style lang="scss" scoped></style>
